import {v4 as uuidv4} from 'uuid';
import {ConsentCategory, hasConsent, isDoNotSellRequested} from './consent';

/**
 * Identity and stop conditions for the pre-consent funnel events.
 *
 * Counsel allowed a narrow pre-consent implementation in exchange for accepting some risk,
 * on six conditions: coarse funnel events only; a random pseudonymous identifier unrelated to
 * email, phone, HubSpot ID or application ID; suppressed IP and geolocation; no form values,
 * full URLs, health data, financial data or qualification results; no autocapture or session
 * replay; and no further events after an Analytics decline, opt-out or GPC signal.
 *
 * The identifier deliberately is NOT `amplifyUserUuid` — that is the application ID, it lives
 * in a rolling one-year cookie, and it is on the lead record, so reusing it would make these
 * events linkable to the person and defeat the basis for collecting them at all.
 */
const SESSION_ID_KEY = 'anonymousAnalyticsSessionId';
const DECLINED_KEY = 'analyticsDeclined';

/** Survives a storage failure; reset only by a later grant. */
let declinedThisVisit = false;

/**
 * A random id for this visit. sessionStorage rather than a variable so a reload mid-funnel
 * does not split the session in two, and rather than localStorage so it cannot follow the
 * visitor to another visit.
 */
export const getAnonymousSessionId = (): string | null => {
    try {
        const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
        if (existing) return existing;

        const created = uuidv4();
        window.sessionStorage.setItem(SESSION_ID_KEY, created);
        return created;
    } catch {
        // Private browsing or blocked site data. Without a stable id the events would be
        // unsequenceable noise, so send nothing.
        return null;
    }
};

/** Remembers an explicit decline so later visits stay silent too. */
export const rememberAnalyticsDeclined = (): void => {
    // Held in memory first. A decline is one of counsel's stop conditions, and it has to hold
    // for this visit even when it cannot be written down: private browsing and blocked site data
    // both throw below, and nothing else halts the coarse stream — hasConsent reads false both
    // before a decision and after a decline, so it cannot tell them apart.
    declinedThisVisit = true;

    try {
        window.localStorage.setItem(DECLINED_KEY, 'true');
    } catch {
        // Only the next visit is affected: it re-asks instead of staying silent.
    }
};

export const clearAnalyticsDeclined = (): void => {
    declinedThisVisit = false;

    try {
        window.localStorage.removeItem(DECLINED_KEY);
    } catch {
        // Nothing to do — the flag is an optimisation over Termly's own state.
    }
};

const hasDeclinedAnalytics = (): boolean => {
    if (declinedThisVisit) return true;

    try {
        return window.localStorage.getItem(DECLINED_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Whether a coarse pre-consent event may be sent.
 *
 * False once the visitor has consented (the identified path takes over), once they have
 * declined, and whenever GPC is present — counsel named all three as stop conditions.
 */
export const maySendAnonymousEvent = (): boolean => {
    if (hasConsent(ConsentCategory.analytics)) return false;
    if (hasDeclinedAnalytics()) return false;
    if (isDoNotSellRequested()) return false;

    return true;
};
